import { useEffect, useState } from 'react';
import { getCombinedAssociationTypeId } from 'customer-data-objects/associations/AssociationCategoryTypes';
import { useHttpClient } from '../client/HttpClientContext';
import { fetchPropertyDefinitions } from './api';
import { conditionalPropertyOptionsRulesCache, propertyValuesCache, associationValuesCache } from '../cache';
import { getToObjectTypeIdsFromConditionalRules, isAssociationField } from './utils';
import { getAssociationAndObjectTypeDefinitions } from '../associationDefinitions/getAssociationAndObjectTypeDefinitions';
import { fetchConditionalPropertyRules as defaultFetchConditionalPropertyRules } from '../v2/conditionalPropertyRules/api';
import { useGetIsUngatedForConditionalAssociations } from '../v2/hooks/useGetIsUngatedForConditionalAssociations';
const usePropertiesFormApi = ({
  objectTypeId,
  objectId,
  fetchConditionalPropertyRules = defaultFetchConditionalPropertyRules,
  enableDependentAssociations = false
}) => {
  const httpClient = useHttpClient();
  const [state, setState] = useState({
    loading: true,
    error: false,
    properties: {},
    conditionalPropertyRules: [],
    conditionalPropertyOptionsRules: [],
    cachedPropertyValues: {},
    associationDefinitionsMap: {},
    cachedAssociationValues: {}
  });
  const hasDependentAssociationsAccess = useGetIsUngatedForConditionalAssociations({
    enableDependentAssociations
  });
  useEffect(() => {
    const propertyDefinitionsPromise = fetchPropertyDefinitions(objectTypeId, httpClient);
    const conditionalPropertyRulesPromise = fetchConditionalPropertyRules(objectTypeId, httpClient, {
      includeAssociationFields: hasDependentAssociationsAccess
    });
    const conditionalPropertyOptionsRulesPromise = conditionalPropertyOptionsRulesCache.get(objectTypeId, httpClient);

    // fetch property values for record being edited or default to {}
    let cachedPropertyValuesPromise;
    if (objectId) {
      cachedPropertyValuesPromise = propertyValuesCache.get({
        objectTypeId,
        objectId
      }, httpClient);
    } else {
      cachedPropertyValuesPromise = Promise.resolve({});
    }
    let associationDefinitionsPromise;
    if (enableDependentAssociations) {
      associationDefinitionsPromise = getAssociationAndObjectTypeDefinitions({
        objectTypeId,
        httpClient
      });
    } else {
      associationDefinitionsPromise = Promise.resolve([]);
    }
    Promise.all([propertyDefinitionsPromise, conditionalPropertyRulesPromise, conditionalPropertyOptionsRulesPromise, cachedPropertyValuesPromise, associationDefinitionsPromise]).then(([properties, conditionalPropertyRules, conditionalPropertyOptionsRules, cachedPropertyValues, associationDefinitionsMap]) => {
      const filteredConditionalPropertyRules = enableDependentAssociations ? conditionalPropertyRules : conditionalPropertyRules.map(rule => {
        return Object.assign({}, rule, {
          dependentFields: rule.dependentFields.filter(field => !isAssociationField(field))
        });
      });
      const associationDefinitionMap = associationDefinitionsMap.reduce((acc, definition) => {
        acc[getCombinedAssociationTypeId(definition)] = definition;
        return acc;
      }, {});
      return {
        properties,
        filteredConditionalPropertyRules,
        conditionalPropertyOptionsRules,
        cachedPropertyValues,
        associationDefinitionsMap: associationDefinitionMap
      };
    }).then(({
      properties,
      filteredConditionalPropertyRules,
      conditionalPropertyOptionsRules,
      cachedPropertyValues,
      associationDefinitionsMap
    }) => {
      const toObjectTypeIds = getToObjectTypeIdsFromConditionalRules(filteredConditionalPropertyRules, associationDefinitionsMap);
      let cachedAssociationValuesPromise;
      if (enableDependentAssociations && objectId) {
        cachedAssociationValuesPromise = associationValuesCache.get({
          objectTypeId,
          objectId,
          toObjectTypeIds
        }, httpClient, false);
      } else {
        cachedAssociationValuesPromise = Promise.resolve({});
      }
      Promise.all([cachedAssociationValuesPromise]).then(([response]) => {
        setState({
          loading: false,
          error: false,
          properties,
          conditionalPropertyRules: filteredConditionalPropertyRules,
          conditionalPropertyOptionsRules,
          cachedPropertyValues,
          associationDefinitionsMap,
          cachedAssociationValues: response
        });
      }).catch(__e => {
        // TODO: Raven (copied from L245)
        setState({
          loading: false,
          error: true,
          properties: {},
          conditionalPropertyRules: [],
          conditionalPropertyOptionsRules: [],
          cachedPropertyValues: {},
          associationDefinitionsMap: {},
          cachedAssociationValues: {}
        });
      });
    }).catch(__e => {
      // TODO: Raven
      setState({
        loading: false,
        error: true,
        properties: {},
        conditionalPropertyRules: [],
        conditionalPropertyOptionsRules: [],
        cachedPropertyValues: {},
        associationDefinitionsMap: {},
        cachedAssociationValues: {}
      });
    });
    // Restricting api calls to initial load. Future changes to flags will not trigger re-load
    // We shall revisit this restriction if a product use-cases arises
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objectTypeId]);
  const {
    loading,
    error,
    properties,
    conditionalPropertyRules,
    conditionalPropertyOptionsRules,
    cachedPropertyValues,
    associationDefinitionsMap,
    cachedAssociationValues
  } = state;
  return {
    loading,
    error,
    properties,
    conditionalPropertyRules,
    conditionalPropertyOptionsRules,
    cachedPropertyValues,
    associationDefinitionsMap,
    cachedAssociationValues
  };
};
export default usePropertiesFormApi;